import React, { useMemo } from 'react'
import { makeStyles, Typography, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  getTieredPackageColor,
  getTieredPackageDescriptionText,
  getTieredPackageFont,
  getTierPrices,
  setSelectedTier,
  TIER_LABELS,
  TIER_ORDER
} from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import CheckIcon from '@material-ui/icons/Check'
import classNames from 'classnames'
import { useQuote } from '../../../../context'
import { activateOnKey, getTierColor, Pill, TierMark } from './shared'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    row: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(2.5),
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr'
      }
    },
    card: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'left',
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: theme.borderRadius.xl,
      background: theme.paintscout.palette.brand.Neutral.P98,
      padding: theme.spacing(3),
      cursor: 'pointer',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': {
        borderColor: theme.paintscout.palette.brand.Neutral.P75
      },
      '&:focus-visible': {
        outline: 'none',
        borderColor: theme.paintscout.palette.brand.Blue.Blue55,
        boxShadow: `0 0 0 3px ${theme.paintscout.palette.semantic.Info.S95}`
      }
    },
    top: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1)
    },
    topRight: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center'
    },
    name: {
      minWidth: 0,
      color: theme.paintscout.palette.brand.Neutral.P30,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(18),
      lineHeight: 1.3
    },
    tagline: {
      color: theme.paintscout.palette.brand.Neutral.P50,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.35,
      marginTop: theme.spacing(0.5)
    },
    // Font styles live on this wrapper and the price <Typography> uses variant="inherit",
    // so it picks them up here. Setting font-size/margin directly on the custom Typography is
    // unreliable — its variant classes win the specificity fight and override them.
    price: {
      margin: theme.spacing(2, 0),
      color: '#354962',
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(24),
      lineHeight: 'normal'
    },
    // Locked (post-accept) read-only states: no pointer affordance, accepted tier stays
    // highlighted, the rest are dimmed.
    cardLocked: {
      cursor: 'default',
      '&:hover': {
        borderColor: theme.paintscout.palette.brand.Neutral.P90
      }
    },
    cardAccepted: {
      borderColor: theme.paintscout.palette.brand.Blue.Blue55,
      boxShadow: `0 0 0 1px ${theme.paintscout.palette.brand.Blue.Blue55}`
    },
    cardDimmed: {
      opacity: 0.55
    },
    button: {
      appearance: 'none',
      border: 'none',
      width: '100%',
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing(1.5, 2),
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 1.4,
      textAlign: 'center',
      color: theme.palette.common.white,
      // Unselected tiers use a light blue; the selected tier overrides to dark blue below.
      background: theme.paintscout.palette.brand.Blue.Blue70,
      cursor: 'pointer',
      transition: 'opacity 0.15s',
      '&:hover': {
        opacity: 0.9
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.paintscout.palette.brand.Blue.Blue55}`,
        outlineOffset: 2
      }
    },
    buttonSelected: {
      // PaintScout brand blue (static) — not theme.palette.primary, which is the account's
      // brand color (e.g. gold) in the customer view.
      background: theme.paintscout.palette.brand.Blue.Blue55,
      cursor: 'default',
      '&:hover': {
        opacity: 1
      }
    },
    featureList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.25),
      marginTop: theme.spacing(2.5)
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.25),
      color: theme.paintscout.palette.brand.Neutral.P30,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.4
    },
    featureCheck: {
      fontSize: theme.typography.pxToRem(18),
      color: theme.paintscout.palette.brand.Neutral.P60,
      marginTop: theme.typography.pxToRem(1),
      flexShrink: 0
    },
    description: {
      color: theme.paintscout.palette.brand.Neutral.P40,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.5,
      marginTop: theme.spacing(2.5),
      // Package descriptions are plain text (see the template editor), so keep their newlines.
      whiteSpace: 'pre-wrap'
    }
  }),
  { name: 'TieredPackageSelector' }
)

/**
 * Customer-facing Good / Better / Best selector, shown between the estimate items and the
 * Total box. Clicking a tier recomputes totals client-side (no save) so the customer can
 * preview how each package affects the quote.
 *
 * Renders only in the customer view — the contractor editor uses the sidebar setup/preview.
 */
export default function TieredPackageSelector() {
  const classes = useStyles({})
  const theme = useTheme()
  const { quote, updateQuote, isCustomerView } = useQuote()
  const { options } = useClientOptions()

  const tieredPackages = quote.tieredPackages
  const tierPrices = useMemo(() => getTierPrices({ quote }), [quote])
  const packageFont = getTieredPackageFont(quote.tieredPackages?.font)
  if (!isCustomerView || !tieredPackages?.enabled || !tierPrices) {
    return null
  }

  // Once the customer has responded (accepted/declined) or the quote has become an invoice, the
  // selected tier is locked in — the preview becomes read-only so the accepted package/price can't
  // be changed. Selection only persists when the customer accepts (acceptQuote sends the full
  // local quote, including the chosen tier and its recalculated totals).
  const isLocked = quote.status?.value === 'accepted' || quote.status?.value === 'declined' || !!quote.is_invoice

  const handleSelect = (tierId: TieredPackageTierId) => {
    if (isLocked || tierId === tieredPackages.selectedTierId) {
      return
    }
    const updatedQuote = setSelectedTier({ quote, options, tierId })
    // In customer-view `updateQuote` is wired to `setQuote` (local state only — see
    // QuoteContextWrapper), so this is a client-side preview and never persists to the DB.
    updateQuote({ quote: updatedQuote })
  }

  return (
    <div className={classes.row}>
      {/* Tiers switched off in the template never reach the customer. */}
      {TIER_ORDER.filter((tierId) => !tieredPackages.tiers[tierId]?.disabled).map((tierId) => {
        const tier = tierPrices[tierId]
        const selected = tier.isSelected
        const name = tier.name || TIER_LABELS[tierId]
        const contentType = tier.contentType ?? 'features'
        const features = (tier.features ?? []).filter((f) => f.trim())
        // Legacy descriptions were authored as rich text; flatten rather than inject HTML.
        const descriptionText = getTieredPackageDescriptionText(tier.description)
        // A tier's stored accent color wins over the positional ramp.
        const tierConfig = tieredPackages.tiers[tierId]
        const accent = tierConfig?.color ? getTieredPackageColor(tierConfig.color).dark : getTierColor(tierId, theme)
        return (
          <div
            key={tierId}
            role={isLocked ? undefined : 'button'}
            tabIndex={isLocked ? undefined : 0}
            className={classNames(
              classes.card,
              isLocked && classes.cardLocked,
              isLocked && selected && classes.cardAccepted,
              isLocked && !selected && classes.cardDimmed
            )}
            onClick={isLocked ? undefined : () => handleSelect(tierId)}
            onKeyDown={isLocked ? undefined : activateOnKey(() => handleSelect(tierId))}
          >
            <div className={classes.top}>
              <Typography className={classes.name} style={{ fontFamily: packageFont.family }}>
                {name}
              </Typography>
              <div className={classes.topRight}>
                {tier.isRecommended ? (
                  <Pill icon={<TierMark color={accent} size={14} />}>
                    {tieredPackages.badgeLabel?.trim() || 'Recommended'}
                  </Pill>
                ) : tier.isBase ? (
                  <Pill icon={<TierMark color={theme.paintscout.palette.brand.Blue.Blue55} size={14} />}>
                    Current Estimate
                  </Pill>
                ) : (
                  <TierMark color={accent} size={22} />
                )}
              </div>
            </div>
            {tier.tagline && <div className={classes.tagline}>{tier.tagline}</div>}

            <div className={classes.price}>
              <Typography variant="inherit" value={tier.price} format={'price'} showUnits={true} />
            </div>

            {/* When locked, only the accepted tier keeps a button (showing "Accepted"); the
                other tiers drop their CTA so it's clear they're no longer selectable. */}
            {(!isLocked || selected) && (
              <button
                type="button"
                className={classNames(classes.button, selected && classes.buttonSelected)}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSelect(tierId)
                }}
                aria-pressed={selected}
              >
                {selected ? (isLocked ? 'Accepted' : 'Selected') : `Choose ${name}`}
              </button>
            )}

            {contentType !== 'description' && features.length > 0 && (
              <div className={classes.featureList}>
                {features.map((feature, i) => (
                  <div key={i} className={classes.featureItem}>
                    <CheckIcon className={classes.featureCheck} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            )}
            {contentType !== 'features' && descriptionText && (
              <div className={classes.description}>{descriptionText}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}
